import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, catchError } from 'rxjs/operators';
import { MeterAbstractService } from '../../../services/meter.abstract.service';
import { MeterItem } from '../../../models/meter-manage.model';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { ProfileResponse } from 'src/app/user/user.model';
import { LinkMeterDialogComponent } from '../link-meter-dialog/link-meter-dialog.component';

@Component({
    selector: 'app-meter-inventory',
    templateUrl: './meter-inventory.component.html',
    styleUrls: ['./meter-inventory.component.scss'],
})
export class MeterInventoryComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private searchSubject$ = new Subject<string>();

    displayedColumns: string[] = ['nodeId', 'nodeType', 'address', 'status', 'users', 'lastMeterReadAt'];
    meters: MeterItem[] = [];
    total = 0;
    pageSize = 20;
    pageIndex = 0;
    loading = false;

    searchTerm = '';
    isSearchMode = false;

    selectedStatus: string = '';
    selectedNodeType: string = '';
    sortBy: 'nodeId' | 'lastMeterReadAt' = 'nodeId';
    sortOrder: 'asc' | 'desc' = 'asc';

    statusOptions = ['ACTIVE', 'INACTIVE', 'UNASSIGNED'];
    nodeTypeOptions = ['WATER_METER'];

    userNameMap: Record<string, string> = {};

    constructor(
        private meterService: MeterAbstractService,
        private userService: UserService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.searchSubject$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((term) => {
            this.searchTerm = term.trim();
            this.pageIndex = 0;
            if (this.searchTerm) {
                this.isSearchMode = true;
                this.doLookup();
            } else {
                this.isSearchMode = false;
                this.loadMeters();
            }
        });

        this.loadMeters();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onSearchChange(value: string): void {
        this.searchSubject$.next(value);
    }

    onStatusChange(status: string): void {
        this.selectedStatus = status;
        this.pageIndex = 0;
        this.loadMeters();
    }

    onNodeTypeChange(nodeType: string): void {
        this.selectedNodeType = nodeType;
        this.pageIndex = 0;
        this.loadMeters();
    }

    onSortChange(sortBy: 'nodeId' | 'lastMeterReadAt'): void {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }
        this.loadMeters();
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadMeters();
    }

    openLinkDialog(): void {
        const dialogRef = this.dialog.open(LinkMeterDialogComponent, {
            width: '600px',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.loadMeters();
            }
        });
    }

    getUserNames(meter: MeterItem): string {
        if (meter.users.length === 0) return '—';
        return meter.users.map((id) => this.userNameMap[id] || id).join(', ');
    }

    formatAddress(meter: MeterItem): string {
        const parts = [meter.address.street];
        if (meter.address.unit) {
            parts.push(`Unit ${meter.address.unit}`);
        }
        parts.push(meter.address.postCode);
        return parts.join(', ');
    }

    private loadMeters(): void {
        this.loading = true;
        this.meterService
            .getMeters({
                page: this.pageIndex + 1,
                limit: this.pageSize,
                status: this.selectedStatus ? (this.selectedStatus as any) : undefined,
                nodeType: this.selectedNodeType ? [this.selectedNodeType] : undefined,
                sortBy: this.sortBy,
                sortOrder: this.sortOrder,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.meters = result.data;
                    this.total = result.total;
                    this.loading = false;
                    this.resolveUserNames(result.data);
                },
                error: () => {
                    this.meters = [];
                    this.total = 0;
                    this.loading = false;
                },
            });
    }

    private doLookup(): void {
        this.loading = true;

        // Send both nodeId and address so the API matches either
        const params: any = { address: this.searchTerm };
        if (/^\d+$/.test(this.searchTerm)) {
            params.nodeId = this.searchTerm;
            delete params.address;
        }

        this.meterService
            .lookupMeters(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (results) => {
                    this.meters = results;
                    this.total = results.length;
                    this.loading = false;
                    this.resolveUserNames(results);
                },
                error: () => {
                    this.meters = [];
                    this.total = 0;
                    this.loading = false;
                },
            });
    }

    private resolveUserNames(meters: MeterItem[]): void {
        const allUserIds: string[] = [];
        meters.forEach((m) => m.users.forEach((id) => allUserIds.push(id)));
        const uniqueIds = [...new Set(allUserIds)].filter((id) => !this.userNameMap[id]);

        if (uniqueIds.length === 0) return;

        const requests = uniqueIds.map((id) => this.userService.getProfileById(id).pipe(catchError(() => of(null as ProfileResponse))));

        forkJoin(requests)
            .pipe(takeUntil(this.destroy$))
            .subscribe((profiles: (ProfileResponse | null)[]) => {
                profiles.forEach((profile: ProfileResponse | null, i: number) => {
                    if (profile) {
                        this.userNameMap[uniqueIds[i]] = `${profile.firstName} ${profile.lastName}`;
                    }
                });
            });
    }
}
